import BaseController from "@base/baseController.js";
import GalleryManager from "./galleryManager.js";
import GalleryPanel from "./components/GalleryPanel.js";
import GalleryDetail from "./components/GalleryDetail.js";
import GalleryEditor from "./components/GalleryEditor.js";
import MoveImagesModal from "./components/MoveImagesModal.js";
import router from "@/services/router.js";
import visitTracker from "@/services/visitTracker.js";
import api from "@api/api.js";
import tagController from "@modules/tag/tagController.js";

/**
 * Gallery module controller: loads galleries and mounts the grid or a detail
 * view. Navigation goes through the router (#/ and #/gallery/:id).
 */
class GalleryController extends BaseController {
  constructor() {
    super({ manager: new GalleryManager() });
    this._loaded = false;
  }

  isAdmin() {
    return this.manager.admin;
  }

  async ensureLoaded() {
    if (!this._loaded) {
      await this.manager.load();
      this._loaded = true;
    }
  }

  /** Force a reload on next access (e.g. after login/language change). */
  invalidate() {
    this._loaded = false;
  }

  /** The public grid — view-only; admin actions live in the Cockpit. */
  async showGrid(container, lang) {
    // Tag names come from the tag module, through its controller — the grid
    // filters on the keys galleries store and only *shows* the translations.
    // They are decoration on a grid that must render regardless, so a failed
    // lookup just means chips fall back to the stored keys.
    const [, tagLabels] = await Promise.all([this.ensureLoaded(), tagController.labels(lang).catch(() => ({}))]);
    const view = new GalleryPanel({
      galleries: this.manager.all(),
      lang,
      heroImage: this.manager.heroImage,
      tagLabels,
      onOpen: (id) => router.go(`/gallery/${id}`),
    });
    await view.mount(container);
    return view;
  }

  async uploadHero(file) {
    const fd = new FormData();
    fd.append("hero", file);
    const res = await api.post("/api/settings/hero", fd, { isForm: true });
    this.manager.heroImage = res?.heroImage ?? null;
    return this.manager.heroImage;
  }

  async deleteHero() {
    await api.del("/api/settings/hero");
    this.manager.heroImage = null;
  }

  /**
   * Open the create/edit overlay. `onDone` lets the caller (the Cockpit's
   * Gallery Manager) refresh itself in place; without one, a full reload is
   * the safe fallback for whatever view happens to be mounted.
   */
  async openEditor(gallery, onDone, lang = "en") {
    // The editor's tag field looks up the shared vocabulary (keys + both
    // display names), so it is fetched before the overlay opens rather than
    // derived from whatever tags happen to be on the loaded galleries. A
    // failure here costs the lookup, not the edit: the field still accepts
    // typed tags, and the backend mints rows for them on save.
    const vocabulary = await tagController.vocabulary(lang).catch(() => []);
    new GalleryEditor({
      gallery,
      vocabulary,
      onDone: onDone || (() => window.location.reload()),
    }).mount(document.body);
  }

  /**
   * Open the move-images overlay (Cockpit → Gallery Manager). Like openEditor,
   * `onDone` lets the caller refresh itself; unlike it, the overlay stays open
   * after a move and re-reads the manager, so the refresh has to happen before
   * it re-renders — hence awaiting onDone inside onMoved rather than firing it
   * and closing.
   */
  openMoveImages(onDone, lang = "en", sourceId = null) {
    new MoveImagesModal({
      galleries: () => this.manager.all(),
      lang,
      sourceId,
      onMove: (from, to, images) => this.moveImages(from, to, images),
      onMoved: async () => {
        if (onDone) return onDone();
        this.invalidate();
        await this.ensureLoaded();
      },
    }).mount(document.body);
  }

  /** Move images between two galleries; resolves to [{ from, to }] per file. */
  async moveImages(sourceId, targetGalleryId, images) {
    const res = await api.post(`/api/galleries/${encodeURIComponent(sourceId)}/images/move`, {
      targetGalleryId,
      images,
    });
    return res?.moved || [];
  }

  async showDetail(container, id, lang) {
    await this.ensureLoaded();
    const gallery = this.manager.get(id);
    if (!gallery) {
      router.go("/");
      return null;
    }
    // One visit per detail view, started here and closed when the view is torn
    // down (route change) or the page goes away (visitTracker's pagehide hook).
    const view = new GalleryDetail({ gallery, lang, onLeave: () => visitTracker.finish() });
    await view.mount(container);
    visitTracker.start(id);
    return view;
  }
}

export default new GalleryController();
